import { toast } from "@/hooks/use-toast";
import { formatEmiMonth, getCurrentEmiMonth } from '@/utils/formatters';
import { AuthService } from '@/integrations/api/services/authService';

// Keep API calls relative so the active environment's proxy routes them.
export const API_BASE_URL = "/api/v1";


//export const API_BASE_URL = import.meta.env.VITE_API_URL + "/api/v1";

// API Response wrapper for consistent error handling
export interface ApiResponse<T = any> {
  data: T | null;
  error: string | null;
  success: boolean;
}

// Get authentication headers
export function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

/** If the response is 401, clear the session and redirect to login. */
export function enforceAuthResponse(response: Response): void {
  if (response.status === 401) {
    AuthService.handleUnauthorized();
  }
}

// Utility function to handle API responses
export async function handleApiResponse<T>(response: Response): Promise<ApiResponse<T>> {
  if (response.status === 401) {
    AuthService.handleUnauthorized();
    return {
      data: null,
      error: 'Session expired. Please sign in again.',
      success: false
    };
  }

  if (response.ok) {
    try {
      const data = await response.json();
      return {
        data,
        error: null,
        success: true
      };
    } catch (error) {
      return {
        data: null,
        error: 'Failed to parse response',
        success: false
      };
    }
  } else {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      // Handle different error response formats
      if (errorData.detail) {
        // If detail is an array (validation errors), join them
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((err: any) => err.msg || err).join('; ');
        } else {
          errorMessage = errorData.detail;
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    
    return {
      data: null,
      error: errorMessage,
      success: false
    };
  }
}

// Utility function to make API requests with error handling
export async function apiRequest<T>(
  url: string, 
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, {
      headers: getAuthHeaders(),
      ...options,
    });
    
    return await handleApiResponse<T>(response);
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Network error',
      success: false
    };
  }
}

/** Preview comments returned on GET /applications/ (type 1, first 3). */
export interface ApplicationListComment {
  comment: string;
  user_name: string;
  play_url?: string;
}

function mapListComments(
  comments?: Array<string | ApplicationListComment> | null
): Array<{ content: string; user_name: string; play_url?: string }> {
  return (comments || []).map((comment) => {
    if (typeof comment === 'string') {
      return { content: comment, user_name: 'Unknown' };
    }
    return {
      content: comment.comment || '',
      user_name: comment.user_name || 'Unknown',
      ...(comment.play_url ? { play_url: comment.play_url } : {}),
    };
  });
}

// Legacy interfaces for backward compatibility
interface ApiApplicationItem {
  application_id: string;
  loan_id?: number;
  payment_id?: number;
  demand_num?: string; // Kept optional for filters still in UI
  applicant_name: string;
  mobile?: string;
  emi_amount: number;
  status?: string; // Removed from list API; optional for backward compat
  emi_month?: string; // Removed from list API; optional for backward compat
  branch: string;
  rm_name: string;
  tl_name: string;
  dealer: string;
  lender: string | null;
  ptp_date: string | null;
  only_visit?: string | null;
  calling_status?: string | null;
  comments?: Array<string | ApplicationListComment>;
  calling_statuses?: {
    applicant?: string;
    co_applicant?: string;
    guarantor?: string;
    reference?: string;
  };
  amount_collected?: number;
  payment_mode?: string;
  loan_amount?: number;
  disbursement_date?: string;
  house_ownership?: string;
  demand_calling_status?: string | null;
  latitude?: number;
  longitude?: number;
  address?: string;
  state?: string;
  vehicle_status_name?: string;
  repossession_date?: string | null;
  repossession_sale_date?: string | null;
  repossession_sale_amount?: number | null;
  current_dpd_bucket?: string | null;
  total_overdue_amount?: number;
  current_overdue_amount?: number;
  total_pos?: number;
  lms_overdue_as_of_1st?: number | null;
  lms_overdue_as_of_today?: number | null;
  app_overdue_as_of_today?: number | null;
  nach_status?: number | string;
  reason?: string | null;
  special_case_tags?: string[];
  rc_number?: string;
  chasis_number?: string;
  tenure?: number;
  // Collection category (E2W / L3 / L5 / Battery today), DB-driven.
  loan_type?: string | null;
}

interface ApiFilteredResponse {
  total: number;
  results: ApiApplicationItem[];
}

export function mapApiResponseToApplication(apiItem: ApiApplicationItem): any {
  return {
    id: apiItem.application_id,
    applicant_id: apiItem.application_id,
    loan_id: apiItem.loan_id, // Map loan_id from API response
    payment_id: apiItem.payment_id, // Map payment_id from API response
    demand_num: apiItem.demand_num, // Map demand_num from API response
    applicant_name: apiItem.applicant_name,
    branch_name: apiItem.branch,
    team_lead: apiItem.tl_name,
    tl_name: apiItem.tl_name,
    rm_name: apiItem.rm_name,
    dealer_name: apiItem.dealer,
    dealer: apiItem.dealer,
    lender_name: apiItem.lender || '',
    lender: apiItem.lender || '',
    lms_status: apiItem.status || 'Unknown',
    field_status: apiItem.status,
    status: apiItem.status,
    emi_amount: apiItem.emi_amount,
    principle_due: 0,
    interest_due: 0,
    demand_date: apiItem.emi_month,
    emi_month: apiItem.emi_month,
    user_id: '1',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ptp_date: apiItem.ptp_date,
    only_visit: apiItem.only_visit ?? null,
    latest_calling_status: apiItem.calling_status || apiItem.demand_calling_status || 'Not Called',
    calling_status: apiItem.calling_status || apiItem.demand_calling_status || 'Not Called',
    recent_comments: mapListComments(apiItem.comments), 
    applicant_mobile: apiItem.mobile || '',
    applicant_address: '',
    co_applicant_name: '',
    co_applicant_mobile: '',
    co_applicant_address: '',
    guarantor_name: '',
    guarantor_mobile: '',
    guarantor_address: '',
    reference_name: '',
    reference_mobile: '',
    reference_address: '',
    fi_location: '',
    repayment: '',
    last_month_bounce: 0,
    collection_rm: '',
    paid_date: '',
    // Map calling statuses from the API response
    applicant_calling_status: apiItem.calling_statuses?.applicant || 'Not Called',
    co_applicant_calling_status: apiItem.calling_statuses?.co_applicant || 'Not Called',
    guarantor_calling_status: apiItem.calling_statuses?.guarantor || 'Not Called',
    reference_calling_status: apiItem.calling_statuses?.reference || 'Not Called',
    disbursement_date: apiItem.disbursement_date || '',
    loan_amount: apiItem.loan_amount || 0,
    vehicle_status: apiItem.vehicle_status_name || '',
    amount_collected: apiItem.amount_collected || 0,
    // Map additional fields from API response
    payment_mode: apiItem.payment_mode || '',
    calling_statuses: apiItem.calling_statuses || {},
    house_ownership: apiItem.house_ownership || '',
    demand_calling_status: apiItem.demand_calling_status || '',
    latitude: apiItem.latitude,
    longitude: apiItem.longitude,
    address: apiItem.address,
    state: apiItem.state,
    repossession_date: apiItem.repossession_date || null,
    repossession_sale_date: apiItem.repossession_sale_date || null,
    repossession_sale_amount: apiItem.repossession_sale_amount || null,
    current_dpd_bucket: apiItem.current_dpd_bucket ?? null,
    total_overdue_amount: apiItem.total_overdue_amount ?? null,
    current_overdue_amount: apiItem.current_overdue_amount ?? null,
    total_pos: apiItem.total_pos ?? null,
    lms_overdue_as_of_1st: apiItem.lms_overdue_as_of_1st ?? null,
    lms_overdue_as_of_today: apiItem.lms_overdue_as_of_today ?? null,
    app_overdue_as_of_today: apiItem.app_overdue_as_of_today ?? null,
    nach_status: apiItem.nach_status !== undefined && apiItem.nach_status !== null 
      ? (typeof apiItem.nach_status === 'string' ? parseInt(apiItem.nach_status, 10) : apiItem.nach_status)
      : undefined,
    reason: apiItem.reason ?? null,
    special_case_tags: (() => {
      const tags = apiItem.special_case_tags || [];
      // Debug logging for special_case_tags
      if (tags.length > 0) {
        console.log(`🔍 Mapping application ${apiItem.application_id} - special_case_tags from API:`, tags);
      }
      return tags;
    })(),
    rc_number: apiItem.rc_number || '',
    chasis_number: apiItem.chasis_number || '',
    tenure: apiItem.tenure ?? null,
    loan_type: apiItem.loan_type ?? null
  };
}

// Legacy functions for backward compatibility
export async function getApplicationDetails(
  applicationId: string,
  emiMonth?: string,
  loanId?: number
): Promise<any> {
  const fetchOne = async (month?: string, id?: number, throwOnError = false) => {
    const params = new URLSearchParams({
      search: applicationId,
      offset: "0",
      limit: "1"
    });
    if (id != null) params.append("loan_id", String(id));
    if (month) params.append("emi_month", month);

    const res = await fetch(`${API_BASE_URL}/applications/?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    enforceAuthResponse(res);
    if (!res.ok) {
      if (throwOnError) throw new Error("Failed to fetch application details");
      return null;
    }

    const data: ApiFilteredResponse = await res.json();
    if (data.results && data.results.length > 0) {
      return mapApiResponseToApplication(data.results[0]);
    }
    return null;
  };

  // List / URL / panel refresh: same single PROS-id search as before.
  if (loanId == null && !emiMonth) {
    const found = await fetchOne(undefined, undefined, true);
    if (found) return found;
    throw new Error("Application not found");
  }

  // Cash Collected only: card month first, then loan_id, then PROS id.
  const found =
    (emiMonth ? await fetchOne(emiMonth, loanId) : null) ||
    (await fetchOne(undefined, loanId)) ||
    (await fetchOne());

  if (found) return found;
  throw new Error("Application not found");
}

export async function getFilteredApplications(
  _emiMonthOrSearch?: string,
  search: string = "",
  offset: number = 0,
  limit: number = 1000
): Promise<{ total: number; applications: any[] }> {
  // Backward compat: old signature was (emiMonth, search, offset, limit)
  const resolvedSearch =
    typeof _emiMonthOrSearch === 'string' && search === '' && !_emiMonthOrSearch.match(/^[A-Za-z]{3}-\d{2}$/)
      ? _emiMonthOrSearch
      : search;

  const params = new URLSearchParams({
    search: resolvedSearch,
    offset: offset.toString(),
    limit: limit.toString()
  });

  const res = await fetch(`${API_BASE_URL}/applications/?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  enforceAuthResponse(res);
  if (!res.ok) throw new Error("Failed to fetch applications");

  const data: ApiFilteredResponse = await res.json();
  return {
    total: data.total,
    applications: data.results.map(mapApiResponseToApplication)
  };
}

export async function getApplicationsList(_emiMonth?: string): Promise<any[]> {
  const params = new URLSearchParams({
    offset: "0",
    limit: "20"
  });

  const res = await fetch(`${API_BASE_URL}/applications/?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  enforceAuthResponse(res);
  if (!res.ok) throw new Error("Failed to fetch applications list");

  const data: ApiFilteredResponse = await res.json();
  return data.results.map(mapApiResponseToApplication);
}

export async function getFilterOptions() {
  const res = await fetch(`${API_BASE_URL}/filters/options`, {
    headers: getAuthHeaders(),
  });
  enforceAuthResponse(res);
  if (!res.ok) throw new Error("Failed to fetch filter options");
  return res.json();
}

export async function getApplicationsFromBackend(
  offset = 0,
  limit = 20,
  additionalFilters: {
    branch?: string[];
    teamLead?: string[];
    rm?: string[];
    sourceTeamLead?: string[];
    sourceRm?: string[];
    dealer?: string[];
    lender?: string[];
    status?: string[];
    nachStatus?: string[];
    repayment?: string[];
    lastMonthBounce?: string[];
    ptpDate?: string[];
    onlyVisitFilter?: string[];
    vehicleStatus?: string[];
    search?: string;
    specialCaseFilter?: string[];
    loanType?: string[];
    loanId?: string | number;
    dpdBucket?: string[];
    ptpWorklist?: string;
    allocationOnly?: boolean;
    /** Same Mon-YY value as collection progress (e.g. Sep-26). Required for ptp_worklist counts to match. */
    emiMonth?: string;
  } = {}
) {
  const params = new URLSearchParams();
  params.append('offset', offset.toString());
  params.append('limit', limit.toString());

  if (additionalFilters.search) {
    params.append('search', additionalFilters.search);
  }
  if (additionalFilters.loanId !== undefined && additionalFilters.loanId !== null) {
    params.append('loan_id', String(additionalFilters.loanId));
  }
  if (additionalFilters.branch && additionalFilters.branch.length > 0) {
    params.append('branch', additionalFilters.branch.join(','));
  }
  if (additionalFilters.teamLead && additionalFilters.teamLead.length > 0) {
    params.append('tl_name', additionalFilters.teamLead.join(','));
  }
  if (additionalFilters.rm && additionalFilters.rm.length > 0) {
    params.append('rm_name', additionalFilters.rm.join(','));
  }
  if (additionalFilters.sourceTeamLead && additionalFilters.sourceTeamLead.length > 0) {
    params.append('source_tl_name', additionalFilters.sourceTeamLead.join(','));
  }
  if (additionalFilters.sourceRm && additionalFilters.sourceRm.length > 0) {
    params.append('source_rm_name', additionalFilters.sourceRm.join(','));
  }
  if (additionalFilters.dealer && additionalFilters.dealer.length > 0) {
    params.append('dealer', additionalFilters.dealer.join(','));
  }
  if (additionalFilters.lender && additionalFilters.lender.length > 0) {
    params.append('lender', additionalFilters.lender.join(','));
  }
  if (additionalFilters.status && additionalFilters.status.length > 0) {
    params.append('status', additionalFilters.status.join(','));
  }
  if (additionalFilters.nachStatus && additionalFilters.nachStatus.length > 0) {
    params.append('nach_status', additionalFilters.nachStatus.join(','));
  }
  if (additionalFilters.repayment && additionalFilters.repayment.length > 0) {
    params.append('demand_num', additionalFilters.repayment.join(','));
  }
  if (additionalFilters.ptpDate && additionalFilters.ptpDate.length > 0) {
    params.append('ptp_date_filter', additionalFilters.ptpDate.join(','));
  }
  if (additionalFilters.onlyVisitFilter && additionalFilters.onlyVisitFilter.length > 0) {
    params.append('only_visit_filter', additionalFilters.onlyVisitFilter.join(','));
  }
  if (additionalFilters.vehicleStatus && additionalFilters.vehicleStatus.length > 0) {
    params.append('vehicle_status', additionalFilters.vehicleStatus.join(','));
  }
  if (additionalFilters.dpdBucket && additionalFilters.dpdBucket.length > 0) {
    params.append('current_dpd_bucket', additionalFilters.dpdBucket.join(','));
  }
  if (additionalFilters.specialCaseFilter && additionalFilters.specialCaseFilter.length > 0) {
    params.append('special_case_filter', additionalFilters.specialCaseFilter.join(','));
  }
  if (additionalFilters.loanType && additionalFilters.loanType.length > 0) {
    params.append('loan_type', additionalFilters.loanType.join(','));
  }
  if (additionalFilters.ptpWorklist) {
    params.append('ptp_worklist', additionalFilters.ptpWorklist);
  }
  if (additionalFilters.emiMonth) {
    params.append('emi_month', additionalFilters.emiMonth);
  }
  if (additionalFilters.allocationOnly) {
    params.append('allocation_only', 'true');
  }

  const response = await fetch(`${API_BASE_URL}/applications/?${params.toString()}`, {
    headers: getAuthHeaders(),
  });
  enforceAuthResponse(response);
  if (!response.ok) throw new Error('Failed to fetch applications');

  console.log('🌐 API Request URL:', `${API_BASE_URL}/applications/?${params.toString()}`);
  console.log('🌐 API Response status:', response.status);

  return await response.json();
}

export async function getPendingApprovals() {
  const response = await fetch(`${API_BASE_URL}/paidpending-approval/`, {
    headers: getAuthHeaders(),
  });
  enforceAuthResponse(response);
  if (!response.ok) throw new Error('Failed to fetch pending approvals');
  const data = await response.json();
  // Return the full response structure
  return data;
}

export async function reviewPendingApproval(requestId: string, payload: any) {
  const response = await fetch(`${API_BASE_URL}/paidpending-approval/${requestId}/review`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });
  enforceAuthResponse(response);
  if (!response.ok) throw new Error('Failed to review request');
  return await response.json();
}

interface CashCollectedFilters {
  search?: string;
  branch?: string[];
  teamLead?: string[];
  rm?: string[];
  sourceTeamLead?: string[];
  sourceRm?: string[];
  dealer?: string[];
  lender?: string[];
  status?: string[];
  repayment?: string[];
  dpdBucket?: string[];
  specialCaseFilter?: string[];
}

export async function getCashCollected(filters: CashCollectedFilters = {}) {
  const params = new URLSearchParams();

  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.branch && filters.branch.length > 0) {
    params.append('branch', filters.branch.join(','));
  }
  if (filters.teamLead && filters.teamLead.length > 0) {
    params.append('tl_name', filters.teamLead.join(','));
  }
  if (filters.rm && filters.rm.length > 0) {
    params.append('rm_name', filters.rm.join(','));
  }
  if (filters.sourceTeamLead && filters.sourceTeamLead.length > 0) {
    params.append('source_tl_name', filters.sourceTeamLead.join(','));
  }
  if (filters.sourceRm && filters.sourceRm.length > 0) {
    params.append('source_rm_name', filters.sourceRm.join(','));
  }
  if (filters.dealer && filters.dealer.length > 0) {
    params.append('dealer', filters.dealer.join(','));
  }
  if (filters.lender && filters.lender.length > 0) {
    params.append('lender', filters.lender.join(','));
  }
  if (filters.status && filters.status.length > 0) {
    params.append('status', filters.status.join(','));
  }
  if (filters.repayment && filters.repayment.length > 0) {
    params.append('repayment_id', filters.repayment.join(','));
  }
  if (filters.dpdBucket && filters.dpdBucket.length > 0) {
    params.append('current_dpd_bucket', filters.dpdBucket.join(','));
  }
  if (filters.specialCaseFilter && filters.specialCaseFilter.length > 0) {
    params.append('special_case_filter', filters.specialCaseFilter.join(','));
  }

  const queryString = params.toString();
  const url = queryString
    ? `${API_BASE_URL}/recent-activity/cash-collected/?${queryString}`
    : `${API_BASE_URL}/recent-activity/cash-collected/`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });
  enforceAuthResponse(response);
  if (!response.ok) throw new Error('Failed to fetch cash collected applications');
  const data = await response.json();
  return data;
}

// Mock client for backward compatibility - this maintains compatibility with existing components
type MockResult<T = any> = { data: T | null | any[]; error: null; count?: number };

class MockQueryBuilder<T = any> implements PromiseLike<MockResult<T>> {
  private _single: boolean = false;
  private _maybeSingle: boolean = false;
  private _selectFields: string[] = [];
  private _whereConditions: Array<{ field: string; operator: string; value: any }> = [];
  private _orderBy: { field: string; ascending: boolean } | null = null;
  private _limitValue: number | null = null;
  private _table: string = '';
  private _operation: 'select' | 'insert' | 'update' | 'delete' | 'upsert' = 'select';
  private _data: any = null;

  from(table: string) {
    this._table = table;
    return this;
  }

  select(...fields: string[]) {
    this._selectFields = fields.length > 0 ? fields : ['*'];
    this._operation = 'select';
    return this;
  }

  insert(data: any) {
    this._data = data;
    this._operation = 'insert';
    return this;
  }

  update(data: any) {
    this._data = data;
    this._operation = 'update';
    return this;
  }

  upsert(data: any) {
    this._data = data;
    this._operation = 'upsert';
    return this;
  }

  delete() {
    this._operation = 'delete';
    return this;
  }

  eq(field: string, value: any) {
    this._whereConditions.push({ field, operator: 'eq', value });
    return this;
  }

  in(field: string, value: any[]) {
    this._whereConditions.push({ field, operator: 'in', value });
    return this;
  }

  gte(field: string, value: any) {
    this._whereConditions.push({ field, operator: 'gte', value });
    return this;
  }

  lte(field: string, value: any) {
    this._whereConditions.push({ field, operator: 'lte', value });
    return this;
  }

  order(field: string, options: { ascending: boolean }) {
    this._orderBy = { field, ascending: options.ascending };
    return this;
  }

  limit(value: number) {
    this._limitValue = value;
    return this;
  }

  single() { 
    this._single = true; 
    return this; 
  }

  maybeSingle() { 
    this._maybeSingle = true; 
    return this; 
  }
  
  then<TResult1 = MockResult<T>, TResult2 = never>(
    onfulfilled?: ((value: MockResult<T>) => TResult1 | PromiseLike<TResult1>) | null,
    _onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): PromiseLike<TResult1 | TResult2> {
    // Simulate a successful response with mock data
    let result: MockResult<T>;

    // For select operations, return mock data based on the table
    if (this._operation === 'select') {
      if (this._table === 'field_status') {
        if (this._single || this._maybeSingle) {
          result = {
            data: { status: '1', calling_status: '1' } as T,
            error: null,
            count: 1
          };
        } else {
          result = {
            data: [{ status: '1', calling_status: '1' }] as T,
            error: null,
            count: 1
          };
        }
      } else if (this._table === 'payment_details') {
        if (this._single || this._maybeSingle) {
          result = {
            data: { amount_collected: '1000' } as T,
            error: null,
            count: 1
          };
        } else {
          result = {
            data: [{ amount_collected: '1000' }] as T,
            error: null,
            count: 1
          };
        }
      } else {
        if (this._single || this._maybeSingle) {
          result = {
            data: {} as T,
            error: null,
            count: 0
          };
        } else {
          result = {
            data: [] as T,
            error: null,
            count: 0
          };
        }
      }
    } else {
      result = {
        data: this._single || this._maybeSingle ? null : [] as T,
        error: null,
        count: 0
      };
    }

    const fulfilled = onfulfilled ?? ((v: any) => v as TResult1);
    const out = fulfilled(result) as any;
    
    // Wrap in a tiny thenable
    return {
      then: (res: any) => {
        const next = res ? res(out) : out;
        return { then: (r: any) => (r ? r(next) : next) } as any;
      }
    } as any;
  }
}

export const client = {
  from<T = any>(_table: string) {
    return new MockQueryBuilder<T>();
  },
  auth: {
    signOut: async () => ({ error: null }),
    signUp: async (_args: any) => ({ data: { user: { id: 'user1', email: _args?.email } }, error: null }),
    admin: {
      createUser: async (_args: any) => ({ data: { user: { id: 'user1', email: _args?.email } }, error: null })
    }
  },
  functions: {
    invoke: async (_fn: string, _args?: any) => ({
      data: { successful: 1, failed: 0, errors: [] },
      error: null
    })
  },
  channel: (_name: string) => ({
    on: () => ({ subscribe: () => ({}) }),
    subscribe: () => ({}),
    unsubscribe: () => ({})
  }),
  removeChannel: (_chan: any) => {}
};
